import { Injectable } from '@angular/core';
import { ApiClient } from '../http/api-client';

export type PayrollTerminationLifecycleStatus =
  | 'scheduled'
  | 'needs_review'
  | 'processing'
  | 'blocked'
  | 'completed'
  | 'dismissed';

export type PayrollTerminationMatchStatus = 'exact' | 'ambiguous' | 'unmatched';

export interface PayrollEmployeeTerminationCase {
  id: string;
  providerLabel: string;
  userId: string | null;
  matchStatus: PayrollTerminationMatchStatus;
  matchMethod: string | null;
  employmentStatus: string;
  payrollParticipationStatus: string;
  effectiveTerminationDate: string | null;
  effectiveRehireDate: string | null;
  lifecycleStatus: PayrollTerminationLifecycleStatus;
  classification: string | null;
  reviewEligible: boolean;
  requiresManualReview: boolean;
  actionable: boolean;
  safeErrorCode: string | null;
  firstObservedAt: string | null;
  lastObservedAt: string | null;
  reviewedAt: string | null;
  processingStartedAt: string | null;
  blockedAt: string | null;
  completedAt: string | null;
  dismissedAt: string | null;
  dismissalReason: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface PayrollEmployeeTerminationPagination {
  count: number;
  hasMore: boolean;
  limit: number;
  offset: number;
}

export interface PayrollEmployeeTerminationListResponse {
  items: PayrollEmployeeTerminationCase[];
  pagination: PayrollEmployeeTerminationPagination;
}

export interface PayrollEmployeeTerminationDetailResponse {
  case: PayrollEmployeeTerminationCase;
}

export interface PayrollTerminationMembershipEvidence {
  membershipId: string;
  teamId: string;
  status: string | null;
  disabled: boolean;
  canReceiveAllowance: boolean;
  removalStatus: string | null;
}

export interface PayrollTerminationMembershipPreview {
  count: number;
  items: PayrollTerminationMembershipEvidence[];
  matchRequired: boolean;
}

export interface PayrollTerminationAllowanceEvidence {
  participationId: string;
  membershipId: string;
  teamId: string;
  status: string;
  activatedAt: string | null;
}

export interface PayrollTerminationAllowancePreview {
  count: number;
  items: PayrollTerminationAllowanceEvidence[];
}

export interface PayrollTerminationSettlementTeamEvidence {
  teamId: string;
  memberRemovalPending: boolean;
  pendingFundingSnapshotCount: number;
  knownPendingSettlement: boolean;
}

export interface PayrollTerminationSettlementPreview {
  teams: PayrollTerminationSettlementTeamEvidence[];
  knownPendingTeamCount: number;
  liveStripeAchStatus: 'not_checked';
  requiresManualReview: boolean;
}

export interface PayrollTerminationDeductionEvidence {
  evidenceId: string;
  recordType: string;
  actionType: string | null;
  teamId: string | null;
  vendorProductId: string | null;
  deductionCode: string | null;
  identityEvidence: string;
  reviewDisposition: 'manual_review_required';
}

export interface PayrollTerminationDeductionPreview {
  currentObligationState: 'unknown';
  evidenceCount: number;
  items: PayrollTerminationDeductionEvidence[];
  matchRequired?: boolean;
  requiresManualReview: boolean;
}

export interface PayrollEmployeeTerminationImpactPreview {
  case: PayrollEmployeeTerminationCase;
  memberships: PayrollTerminationMembershipPreview;
  monthlyAllowanceParticipation: PayrollTerminationAllowancePreview;
  pendingSettlement: PayrollTerminationSettlementPreview;
  payrollDeductions: PayrollTerminationDeductionPreview;
}

export interface PayrollEmployeeTerminationListQuery {
  lifecycleStatus?: PayrollTerminationLifecycleStatus;
  limit?: number;
  offset?: number;
}

export type PayrollTerminationProcessingState =
  | 'ready_for_removal'
  | 'awaiting_removal'
  | 'awaiting_final_removal'
  | 'ready_to_complete'
  | 'blocked';

export interface PayrollTerminationProcessingBlocker {
  code: string;
  category: string;
  message: string;
}

export interface PayrollTerminationProcessingReadiness {
  caseId: string;
  state: PayrollTerminationProcessingState;
  eligible: boolean;
  blockers: PayrollTerminationProcessingBlocker[];
  membershipCounts: {
    exactCompanyMembershipCount: number;
    remainingMembershipCount: number;
    pendingFinalRemovalCount: number;
  };
  affectedTeamIds: string[];
  completionConditions: {
    companyDepartureRecorded: boolean;
    noExactCompanyMemberships: boolean;
    noOtherCompanyMemberships: boolean;
    noStoredPayrollDeductionEvidence: boolean;
    wallitBrandRestored: boolean;
    brandRetained?: boolean;
  };
  payrollDeductionCancellation: {
    state: 'none' | 'manual_review' | 'actionable' | 'submitted' | 'outcome_unknown';
    canSubmit: boolean;
  };
}

export interface PayrollTerminationDeductionCancellationResponse {
  caseId: string;
  status: 'submitted';
  message: 'Cancellation submitted';
  effectiveDate: string;
  idempotent: boolean;
}

export interface PayrollTerminationCompletionResponse {
  caseId: string;
  lifecycleStatus: 'completed';
  completedAt: string | null;
}

export interface PayrollTerminationResolutionResponse {
  caseId: string;
  lifecycleStatus: 'dismissed';
  dismissedAt: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class PayrollEmployeeTerminationsService {
  constructor(private restangular: ApiClient) {}

  listCases(
    companyId: string,
    query: PayrollEmployeeTerminationListQuery = {},
  ): Promise<PayrollEmployeeTerminationListResponse> {
    return this.companyCases(companyId)
      .customGET('', query)
      .toPromise() as Promise<PayrollEmployeeTerminationListResponse>;
  }

  getCase(companyId: string, caseId: string): Promise<PayrollEmployeeTerminationDetailResponse> {
    return this.companyCases(companyId)
      .one(caseId)
      .get()
      .toPromise() as Promise<PayrollEmployeeTerminationDetailResponse>;
  }

  getImpactPreview(companyId: string, caseId: string): Promise<PayrollEmployeeTerminationImpactPreview> {
    return this.companyCases(companyId)
      .one(caseId)
      .one('impact-preview')
      .get()
      .toPromise() as Promise<PayrollEmployeeTerminationImpactPreview>;
  }

  getProcessingReadiness(companyId: string, caseId: string): Promise<PayrollTerminationProcessingReadiness> {
    return this.companyCases(companyId)
      .one(caseId)
      .one('processing-readiness')
      .get()
      .toPromise() as Promise<PayrollTerminationProcessingReadiness>;
  }

  completeTerminationReview(companyId: string, caseId: string): Promise<PayrollTerminationCompletionResponse> {
    return this.companyCases(companyId)
      .one(caseId)
      .customPOST({}, 'complete')
      .toPromise() as Promise<PayrollTerminationCompletionResponse>;
  }

  resolveTerminationReview(companyId: string, caseId: string): Promise<PayrollTerminationResolutionResponse> {
    return this.companyCases(companyId)
      .one(caseId)
      .customPOST({}, 'resolve')
      .toPromise() as Promise<PayrollTerminationResolutionResponse>;
  }

  cancelPayrollDeduction(
    companyId: string,
    caseId: string,
  ): Promise<PayrollTerminationDeductionCancellationResponse> {
    return this.companyCases(companyId)
      .one(caseId)
      .customPOST({}, 'payroll-deduction-cancellation')
      .toPromise() as Promise<PayrollTerminationDeductionCancellationResponse>;
  }

  private companyCases(companyId: string): any {
    return this.restangular
      .one('payroll')
      .one('company', companyId)
      .one('employee-terminations');
  }
}
